import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Users, UserPlus, Shield, Eye, Edit3, Trash2, MessageSquare, Upload, Search, Crown } from 'lucide-react';
import { toast, confirm as confirmModal } from '../ui/Toast';

const ROLE_CONFIG = {
  creator: { label: 'CRÉATEUR', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  admin: { label: 'Admin', color: '#2563eb', bg: 'rgba(37,99,235,0.12)' },
  contributor: { label: 'Contributeur', color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
  reader: { label: 'Lecteur', color: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
};

const ACTIVITY_ICONS = {
  message: <MessageSquare size={13} style={{ color: '#3b82f6' }} />,
  upload: <Upload size={13} style={{ color: '#22c55e' }} />,
};

const ProjectTeam = ({ projectId, user }) => {
  const [members, setMembers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [inviteRole, setInviteRole] = useState('contributor');
  const [inviting, setInviting] = useState(false);
  const searchTimeout = useRef(null);

  const token = () => localStorage.getItem('oracle_token');
  const headers = () => ({ Authorization: `Bearer ${token()}` });

  const isCreator = members.find(m => m.user_id === user?.id)?.role === 'creator';
  const isAdmin = ['creator', 'admin'].includes(members.find(m => m.user_id === user?.id)?.role);

  useEffect(() => {
    if (projectId) {
      loadMembers();
      loadActivity();
    }
  }, [projectId]);

  const loadMembers = async () => {
    setLoading(true);
    try {
      setLoadError(null);
      const res = await axios.get(`/api/projects/${projectId}/team`, { headers: headers() });
      if (res.data.success) setMembers(res.data.members || []);
    } catch (e) {
      console.error('[Team] Erreur chargement membres:', e);
      setLoadError(e.response?.data?.message || e.response?.data?.error || "Accès insuffisant à l'équipe de ce projet.");
    } finally {
      setLoading(false);
    }
  };

  const loadActivity = async () => {
    try {
      const res = await axios.get(`/api/projects/${projectId}/team/activity`, { headers: headers() });
      if (res.data.success) setActivity(res.data.activity || []);
    } catch (e) {
      console.error('[Team] Erreur chargement activité:', e);
    }
  };

  const handleSearch = (q) => {
    setSearchQuery(q);
    setSelectedUser(null);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (q.trim().length < 2) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await axios.get(`/api/users/search?q=${encodeURIComponent(q)}`, { headers: headers() });
        const existingIds = members.map(m => m.user_id);
        setSearchResults((res.data || []).filter(u => !existingIds.includes(u.id)));
      } catch (e) { setSearchResults([]); }
    }, 300);
  };

  const handleInvite = async () => {
    if (!selectedUser || inviting) return;
    setInviting(true);
    try {
      await axios.post(`/api/projects/${projectId}/team/invite`, {
        user_id: selectedUser.id, role: inviteRole
      }, { headers: headers() });
      toast.success(`${selectedUser.username} invité comme ${ROLE_CONFIG[inviteRole]?.label}`);
      setSelectedUser(null);
      setSearchQuery('');
      setSearchResults([]);
      await loadMembers();
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.error || 'Erreur invitation');
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`/api/projects/${projectId}/team/${userId}/role`, { role: newRole }, { headers: headers() });
      toast.success('Rôle mis à jour');
      await loadMembers();
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.error || 'Erreur changement rôle');
    }
  };

  const handleRemove = async (userId, username) => {
    const confirmed = await confirmModal(
      `Retirer ${username} du projet ?`,
      { danger: true, confirmText: 'Retirer' }
    );
    if (!confirmed) return;
    try {
      await axios.delete(`/api/projects/${projectId}/team/${userId}`, { headers: headers() });
      toast.success(`${username} retiré du projet`);
      await loadMembers();
    } catch (e) {
      toast.error(e.response?.data?.message || e.response?.data?.error || 'Erreur suppression');
    }
  };

  const getInitials = (name) => (name || '??').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  const timeAgo = (date) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'maintenant';
    if (mins < 60) return `il y a ${mins}min`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `il y a ${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `il y a ${days}j`;
  };

  const cardStyle = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: 20 };

  if (loading && members.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-secondary)' }}>
        <div style={{ width: 32, height: 32, border: '2px solid var(--card-border)', borderTop: '2px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
        Chargement équipe...
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 900 }}>

      {/* Section 1: Membres */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
          <Users size={16} style={{ color: '#3b82f6' }} /> Membres du projet
          <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>({members.length})</span>
        </div>

        {loadError && (
          <div style={{ padding: 12, marginBottom: 12, borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', color: '#ef4444', fontSize: 13 }}>
            {loadError}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {members.map(m => {
            const rc = ROLE_CONFIG[m.role] || ROLE_CONFIG.reader;
            return (
              <div key={m.user_id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                background: 'var(--input-bg)', borderRadius: 8, border: '1px solid var(--card-border)'
              }}>
                {/* Avatar */}
                <div style={{
                  width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                  background: 'linear-gradient(135deg, #2563eb, #f59e0b)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontSize: 12, fontWeight: 700
                }}>
                  {getInitials(m.full_name || m.username)}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.full_name || m.username}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                    @{m.username} {m.email && `• ${m.email}`}
                  </div>
                </div>

                {/* Role badge / selector */}
                {m.role === 'creator' ? (
                  <span style={{
                    padding: '3px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700,
                    background: rc.bg, color: rc.color, display: 'flex', alignItems: 'center', gap: 4
                  }}>
                    <Crown size={10} /> {rc.label}
                  </span>
                ) : isCreator ? (
                  <select value={m.role}
                    onChange={e => handleRoleChange(m.user_id, e.target.value)}
                    style={{
                      padding: '4px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                      background: rc.bg, color: rc.color, border: `1px solid ${rc.color}30`,
                      cursor: 'pointer'
                    }}>
                    <option value="admin">Admin</option>
                    <option value="contributor">Contributeur</option>
                    <option value="reader">Lecteur</option>
                  </select>
                ) : (
                  <span style={{
                    padding: '3px 10px', borderRadius: 6, fontSize: 10, fontWeight: 600,
                    background: rc.bg, color: rc.color
                  }}>
                    {rc.label}
                  </span>
                )}

                {/* Remove button */}
                {isAdmin && m.role !== 'creator' && m.user_id !== user?.id && (
                  <button onClick={() => handleRemove(m.user_id, m.username)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#ef4444', display: 'flex', opacity: 0.6, transition: 'opacity 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = 1}
                    onMouseLeave={e => e.currentTarget.style.opacity = 0.6}
                    title="Retirer du projet"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Section 2: Inviter */}
      {isAdmin && (
        <div style={cardStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
            <UserPlus size={16} style={{ color: '#22c55e' }} /> Inviter un membre
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            {/* Search */}
            <div style={{ flex: 1, position: 'relative' }}>
              <div style={{ position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                <input
                  type="text" value={searchQuery}
                  onChange={e => handleSearch(e.target.value)}
                  placeholder="Rechercher un utilisateur..."
                  style={{
                    width: '100%', padding: '10px 10px 10px 32px', borderRadius: 8,
                    border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                    color: 'var(--text)', fontSize: 13, outline: 'none'
                  }}
                  onFocus={e => e.target.style.borderColor = '#3b82f6'}
                  onBlur={e => { setTimeout(() => setSearchResults([]), 200); e.target.style.borderColor = 'var(--card-border)'; }}
                />
              </div>
              {/* Dropdown */}
              {searchResults.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                  marginTop: 4, background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                  borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.2)', maxHeight: 200, overflowY: 'auto'
                }}>
                  {searchResults.map(u => (
                    <div key={u.id} onMouseDown={() => { setSelectedUser(u); setSearchQuery(u.full_name || u.username); setSearchResults([]); }}
                      style={{
                        padding: '8px 12px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 8,
                        borderBottom: '1px solid var(--card-border)'
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.06)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{
                        width: 28, height: 28, borderRadius: 6,
                        background: 'linear-gradient(135deg, #2563eb, #f59e0b)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontSize: 10, fontWeight: 700
                      }}>
                        {getInitials(u.full_name || u.username)}
                      </div>
                      <div>
                        <div style={{ fontWeight: 500, color: 'var(--text)' }}>{u.full_name || u.username}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>@{u.username}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Role selector */}
            <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}
              style={{
                padding: '10px 12px', borderRadius: 8, fontSize: 13,
                border: '1px solid var(--card-border)', background: 'var(--input-bg)', color: 'var(--text)'
              }}>
              <option value="admin">Admin</option>
              <option value="contributor">Contributeur</option>
              <option value="reader">Lecteur</option>
            </select>

            {/* Invite button */}
            <button onClick={handleInvite} disabled={!selectedUser || inviting}
              style={{
                padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                background: selectedUser && !inviting ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'var(--card-border)',
                color: 'white', border: 'none', cursor: selectedUser && !inviting ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap'
              }}>
              <UserPlus size={14} />
              {inviting ? '...' : 'Inviter'}
            </button>
          </div>

          {/* Selected user preview */}
          {selectedUser && (
            <div style={{
              marginTop: 10, padding: '8px 12px', background: 'rgba(34,197,94,0.06)',
              border: '1px solid rgba(34,197,94,0.15)', borderRadius: 6, fontSize: 12
            }}>
              Inviter <strong>{selectedUser.full_name || selectedUser.username}</strong> (@{selectedUser.username}) comme <strong>{ROLE_CONFIG[inviteRole]?.label}</strong>
            </div>
          )}
        </div>
      )}

      {/* Section 3: Activité récente */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
          <MessageSquare size={16} style={{ color: '#8b5cf6' }} /> Activité récente
        </div>

        {activity.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 12 }}>
            Aucune activité récente
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 300, overflowY: 'auto' }}>
            {activity.map((a, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                borderRadius: 6, fontSize: 11
              }}>
                {ACTIVITY_ICONS[a.type] || <MessageSquare size={13} />}
                <span style={{ color: '#3b82f6', fontWeight: 500 }}>{a.full_name || a.username || a.user_id}</span>
                <span style={{ color: 'var(--text-secondary)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.description}
                </span>
                <span style={{ color: 'var(--text-muted)', flexShrink: 0, fontSize: 10 }}>
                  {timeAgo(a.created_at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectTeam;
